import moment from 'moment'
import { create } from 'zustand'
import { wipAiFixture } from '../mocks/wipAiFixture'
import { computeFindings } from '../lib/findings'
import {
  getWipAiConversation,
  generateWipAiRevision as apiGenerateWipAiRevision,
  sendWipAiMessage as apiSendWipAiMessage,
  submitWipAiRevision as apiSubmitWipAiRevision,
  restoreWipAiRevision as apiRestoreWipAiRevision,
  discardWipAiProposal as apiDiscardWipAiProposal,
} from '../../../entities/wipReportAi/service'

export const LIVE_CANVAS_ID = 'live_canvas'

/** Placeholder revision id until live data or API revisions exist (top bar only) */
export const WIP_AI_PENDING_REV_ID = '__wip_ai_pending__'

export function uid(prefix = 'id') {
  return `${prefix}_${Math.random()
    .toString(36)
    .slice(2, 9)}`
}

function timeLabelFromIso(iso) {
  if (!iso) return ''
  return moment(iso).format('MMM DD')
}

function mapRevisionFromApi(r) {
  return {
    id: r.id,
    revisionNumber: r.revisionNumber,
    source: r.source,
    caption: r.caption || '',
    timeLabel: timeLabelFromIso(r.createdAt),
    createdAt: r.createdAt,
    parentRevisionId: r.parentRevisionId,
    messageId: r.messageId,
    snapshot: {
      oration: r.snapshot?.oration || '',
      budgetTrend: r.snapshot?.budgetTrend || 'On Target',
      wipData: r.snapshot?.wipData,
      keyDates: r.snapshot?.keyDates || [],
      findings: r.snapshot?.findings || [],
    },
  }
}

function mapMessageFromApi(m) {
  return {
    id: m.id,
    role: m.role,
    content: m.content || '',
    findings: m.findings || [],
    citations: (m.citations || []).map(c => ({
      path: c.path,
      value: c.value == null ? '' : String(c.value),
    })),
    proposedChanges: m.proposedChanges || null,
    producedRevisionId: m.producedRevisionId,
    producedRevLabel: m.producedRevLabel,
  }
}

function applyConversationDto(conv, set) {
  const revisions = (conv.revisions || []).map(mapRevisionFromApi)
  const messages = (conv.messages || []).map(mapMessageFromApi)
  const latestId = revisions.length ? revisions[revisions.length - 1].id : null
  set({
    conversation: {
      id: conv.id,
      projectId: conv.projectId,
      month: conv.month,
      createdAt: conv.createdAt,
      messages,
    },
    revisions,
    selectedRevId: latestId,
    aiHydrating: false,
  })
}

function emptyConversation(projectId, month) {
  return {
    id: null,
    projectId,
    month,
    messages: [],
    createdAt: new Date().toISOString(),
  }
}

function seedInitialState() {
  const F = wipAiFixture
  const month = F.currentMonth
  const rpt = F.wipReport.monthlyWipReports.find(r => r.month === month)
  const findings = computeFindings({
    wipReport: F.wipReport,
    keyDates: F.keyDates,
    assignments: F.assignments,
    month,
    today: F.today,
  })

  const rev1 = {
    id: 'rev_1',
    revisionNumber: 1,
    source: 'ai',
    caption: 'Initial draft from AI',
    timeLabel: 'Apr 08',
    createdAt: '2026-04-08T09:14:00Z',
    snapshot: {
      oration: `Through the first week of April, the team opened *${F.project.name}* discovery and began deposition prep. Early logged fees total approximately **$8,900** against the **$80,000** monthly budget. Activity is concentrated on doc review and initial memo drafting, consistent with the plan at month-start.\n\nKey upcoming milestone: the **Expert Report** due **April 28**. Pacing will be revisited mid-month.`,
      budgetTrend: 'On Target',
      wipData: rpt,
      keyDates: F.keyDates,
      findings: [],
    },
  }

  const rev2 = {
    id: 'rev_2',
    revisionNumber: 2,
    source: 'ai+user',
    caption: 'Mid-month: flagged acceleration',
    timeLabel: 'Apr 15',
    createdAt: '2026-04-15T16:02:00Z',
    snapshot: {
      oration: `Mid-month for *${F.project.name}*: actuals to date stand at approximately **$44,600**, tracking slightly ahead of the **$40,000** mid-month prorated budget. Work accelerated around the deposition-prep cycle, with concentrated effort from Priya Shah and Jordan Ellis.\n\nWe expect the pace to moderate once the **Expert Report** is submitted on **April 28**. Client communication is planned for early next week.`,
      budgetTrend: 'Ahead',
      wipData: rpt,
      keyDates: F.keyDates,
      findings: [],
    },
  }

  const rev3oration = `During **April 2026** the team advanced discovery and pre-deposition preparation on *${F.project.name}*. Actuals to date total **$89,521** against a monthly budget of **$80,000**, placing the matter at **112% pacing** — **$9,521 over budget**.\n\nWork focused on doc review, memo drafting, deposition preparation, and the initial expert report, which is due **April 28**. Workload concentration on **Priya Shah** and **Jordan Ellis** exceeded weekly assignments this period, reflecting the accelerated pre-deposition schedule.\n\nLooking forward, we recommend a scope conversation ahead of the **Status Conference on May 12** to align the remaining budget against expert testimony and the 30(b)(6) deposition scheduled for early June.`

  const rev3 = {
    id: 'rev_3',
    revisionNumber: 3,
    source: 'ai+user',
    caption: 'Over-budget call, ready to export',
    timeLabel: 'Apr 22',
    createdAt: '2026-04-22T11:40:00Z',
    snapshot: {
      oration: rev3oration,
      budgetTrend: 'Over Budget',
      wipData: rpt,
      keyDates: F.keyDates,
      findings,
    },
  }

  const pendingOration = `${rev3oration}\n\nWe recommend confirming next month's budget envelope before expert testimony ramps.`

  const conversation = {
    id: 'conv_seed_wip_ai',
    projectId: F.project._id,
    month,
    createdAt: '2026-04-01T09:00:00Z',
    messages: [
      {
        id: 'msg_seed_1',
        role: 'assistant',
        content: `Here's a first draft based on week-one activity. We're on pace with the **$80,000** budget for the month.`,
        findings: [],
        citations: [{ path: 'submittedHours.staffHours', value: '40' }],
        proposedChanges: { oration: rev1.snapshot.oration, budgetTrend: 'On Target' },
        producedRevisionId: 'rev_1',
        producedRevLabel: 1,
      },
      {
        id: 'msg_seed_2',
        role: 'user',
        content: 'Looks fine. Let me know if we start drifting.',
      },
      {
        id: 'msg_seed_3',
        role: 'assistant',
        content: `Mid-month check-in. **$44,600 logged** against the $40,000 prorated target — **~10% ahead**. Deposition prep for the Apr 28 deadline is driving it.`,
        findings: [],
        citations: [
          { path: 'hoursDetails.Priya', value: '20h' },
          { path: 'hoursDetails.Jordan', value: '19h' },
        ],
        proposedChanges: { oration: rev2.snapshot.oration, budgetTrend: 'Ahead' },
        producedRevisionId: 'rev_2',
        producedRevLabel: 2,
      },
      {
        id: 'msg_seed_4',
        role: 'user',
        content: "What's our actual pacing now? And who's over their assignment?",
      },
      {
        id: 'msg_seed_5',
        role: 'assistant',
        content: `Pacing is now at **112%** — we crossed the budget line. Two team members are over their weekly assignments:\n\n- **Priya Shah**: 35h logged vs. 10h/wk assignment (≈80% over)\n- **Jordan Ellis**: 45h logged vs. 8h/wk assignment (≈30% over)\n\nI'd flip *Budget Trend* to **Over Budget** and rewrite the narrative to cover the over-run, the drivers (deposition prep + expert report), and the recommended scope conversation before the May 12 Status Conference.`,
        findings,
        citations: [
          { path: 'wipPacing', value: '112%' },
          { path: 'submittedHours.staffRate', value: '85400' },
          { path: 'assignments.Priya', value: '10h/wk' },
          { path: 'assignments.Jordan', value: '8h/wk' },
        ],
        proposedChanges: {
          oration: rev3oration,
          budgetTrend: 'Over Budget',
          keyDates: F.keyDates,
        },
        producedRevisionId: 'rev_3',
        producedRevLabel: 3,
      },
      {
        id: 'msg_pending_prop',
        role: 'assistant',
        content:
          'Optional refinement: add a billing checkpoint before June expert work. Submit to save as the next revision.',
        proposedChanges: {
          oration: pendingOration,
          budgetTrend: 'Over Budget',
          keyDates: F.keyDates,
        },
        citations: [{ path: 'wipPacing', value: '112%' }],
      },
    ],
  }

  return {
    revisions: [rev1, rev2, rev3],
    selectedRevId: 'rev_2',
    conversation,
  }
}

function getLatestRev(revisions) {
  return revisions[revisions.length - 1]
}

/** Webpack inlines `process.env.NODE_ENV` — do not use `import.meta` (not parsed by this bundler). */
const allowFixture =
  (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') ||
  (typeof process !== 'undefined' && process.env.NODE_ENV === 'test')

function baseRuntimeState() {
  return {
    gqlClient: null,
    projectId: null,
    month: null,
    revisions: [],
    selectedRevId: null,
    conversation: {
      id: null,
      projectId: null,
      month: null,
      messages: [],
      createdAt: '',
    },
    generating: false,
    aiHydrating: false,
    toastMessage: null,
  }
}

export const useWipAiStore = create((set, get) => ({
  ...baseRuntimeState(),

  clearToastMessage: () => set({ toastMessage: null }),

  resetToFixture: () => {
    if (!allowFixture) return
    const { gqlClient, projectId, month } = get()
    set({
      ...seedInitialState(),
      generating: false,
      toastMessage: null,
      aiHydrating: false,
      gqlClient,
      projectId,
      month,
    })
  },

  hydrateWipAi: async ({ client, projectId, month }) => {
    if (!client || !projectId || !month) {
      set({ ...baseRuntimeState() })
      return
    }
    set({
      gqlClient: client,
      projectId,
      month,
      aiHydrating: true,
    })
    try {
      const conv = await getWipAiConversation(client, projectId, month)
      if (!conv) {
        set({
          revisions: [],
          conversation: emptyConversation(projectId, month),
          selectedRevId: null,
          aiHydrating: false,
        })
        return
      }
      applyConversationDto(conv, set)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
      set({
        revisions: [],
        conversation: emptyConversation(projectId, month),
        selectedRevId: null,
        aiHydrating: false,
        toastMessage: 'Failed to load WIP assistant',
      })
    }
  },

  selectRev: id => set({ selectedRevId: id }),

  returnToLatest: () => {
    const { revisions } = get()
    if (!revisions.length) return {}
    const latest = getLatestRev(revisions)
    set({ selectedRevId: latest.id })
    return { toast: `Returned to Rev ${latest.revisionNumber}` }
  },

  restoreRevision: async () => {
    const { gqlClient, selectedRevId } = get()
    if (!gqlClient || !selectedRevId) return {}
    set({ generating: true })
    try {
      const conv = await apiRestoreWipAiRevision(gqlClient, selectedRevId)
      applyConversationDto(conv, set)
      const { revisions: revs } = get()
      const latest = revs[revs.length - 1]
      if (latest) set({ selectedRevId: latest.id })
      set({ generating: false, toastMessage: `Rev ${latest.revisionNumber} created (restore)` })
      return { toast: `Rev ${latest.revisionNumber} created` }
    } catch (e) {
      set({ generating: false, toastMessage: e.message || 'Restore failed' })
      return {}
    }
  },

  generateSummary: async () => {
    const { gqlClient, projectId, month } = get()
    if (!gqlClient || !projectId || !month) return
    set({ generating: true })
    try {
      const conv = await apiGenerateWipAiRevision(gqlClient, projectId, month)
      applyConversationDto(conv, set)
      const { revisions: revs } = get()
      const latest = revs[revs.length - 1]
      if (latest) set({ selectedRevId: latest.id })
      set({ generating: false, toastMessage: `Rev ${latest.revisionNumber} created by AI` })
    } catch (e) {
      set({ generating: false, toastMessage: e.message || 'Generate failed' })
    }
  },

  sendMessage: async text => {
    const { gqlClient, projectId, month } = get()
    if (!gqlClient || !projectId || !month) return
    set({ generating: true })
    try {
      const conv = await apiSendWipAiMessage(gqlClient, projectId, month, text)
      applyConversationDto(conv, set)
      set({ generating: false })
    } catch (e) {
      set({ generating: false, toastMessage: e.message || 'Send failed' })
    }
  },

  submitRevision: async msgId => {
    const { gqlClient, projectId, month } = get()
    if (!gqlClient || !projectId || !month) return {}
    set({ generating: true })
    try {
      const conv = await apiSubmitWipAiRevision(gqlClient, projectId, month, msgId)
      applyConversationDto(conv, set)
      const { revisions: revs } = get()
      const latest = revs[revs.length - 1]
      if (latest) set({ selectedRevId: latest.id })
      set({ generating: false, toastMessage: `Rev ${latest.revisionNumber} submitted` })
      return { toast: `Rev ${latest.revisionNumber} submitted` }
    } catch (e) {
      set({ generating: false, toastMessage: e.message || 'Submit failed' })
      return {}
    }
  },

  discardProposal: async msgId => {
    const { gqlClient, projectId, month } = get()
    if (!gqlClient || !projectId || !month) return
    set({ generating: true })
    try {
      const conv = await apiDiscardWipAiProposal(gqlClient, projectId, month, msgId)
      applyConversationDto(conv, set)
      set({ generating: false })
    } catch (e) {
      set({ generating: false, toastMessage: e.message || 'Discard failed' })
    }
  },
}))
